/**
 * Use Global Config Hook
 *
 * Custom hooks for fetching global app configuration with TanStack Query.
 * Handles caching, refetching, and error states automatically.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { fetchMobileConfig, fetchWebConfig } from '@/lib/api';
import { useAuth } from '@/state/auth-context';
import type { GlobalConfigResponse, ConfigSource } from '@/types/global-config';

/**
 * Use Global Config
 * Fetches global app configuration based on the source (mobile or web)
 *
 * @param source - The config source: 'mobile' for mobile app config, 'web' for web CMS config
 * @returns TanStack Query result with global config data
 */
export function useGlobalConfig(source: ConfigSource): UseQueryResult<GlobalConfigResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['globalConfig', source],
    queryFn: async () => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return source === 'mobile' ? fetchMobileConfig(token) : fetchWebConfig(token);
    },
    staleTime: 30000, // 30 seconds - data is considered fresh for 30s
    gcTime: 300000, // 5 minutes - cache is garbage collected after 5 minutes
    retry: 2, // Retry failed requests twice
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnReconnect: true, // Refetch when network reconnects
  });
}

/**
 * Use Mobile Config
 * Convenience hook for fetching mobile app configuration
 *
 * @returns TanStack Query result with mobile config data
 */
export function useMobileConfig(): UseQueryResult<GlobalConfigResponse> {
  return useGlobalConfig('mobile');
}

/**
 * Use Web Config
 * Convenience hook for fetching web CMS configuration
 *
 * @returns TanStack Query result with web config data
 */
export function useWebConfig(): UseQueryResult<GlobalConfigResponse> {
  return useGlobalConfig('web');
}
